// @ts-check

import {
  BarChartOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  PlusCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import * as turf from "@turf/turf";
import {
  Alert, Badge, Button, Col, Descriptions, Dropdown, Empty, Form, Image, Input, Modal, Menu,
  notification, PageHeader, Radio, Row, Select, Skeleton, Space, Switch, Table, Tag, Tooltip,
  Transfer, Typography, Upload, Grid,
} from "antd";
import useModal from "antd/lib/modal/useModal";
import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IoMapOutline } from "react-icons/io5";
import { RxOpenInNewWindow } from "react-icons/rx";
import { MapContainer, useMap } from "react-leaflet";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import tr from "../i18n";
import useWindowSize from "../utils/WindowSize";
import { DeviceProvider } from "./device-context";
import { MapsProvider, useMaps } from "./maps-context";
import { GARDEN_OPTIMIZATION_CHANGE_EVENT, OptimizationProvider } from "./optimization-context";

const GardenContext = /** @type {import("react").Context<import("../types/global").GardenContextValue | undefined>} */ (createContext(undefined));

export function useGarden() {
  const context = useContext(GardenContext);
  if (!context) throw new Error("useGarden must be used within a GardenProvider");
  return context;
}

/**
 * GardenProvider
 * @param {import("react").PropsWithChildren<{}>} props
 * @returns {import("react").ReactElement | null}
 */
export default function GardenProvider({ children }) {
  const { id: gardenId } = useParams();
  const [isOptimizationsPanelOpened, setIsOptimizationsPanelOpened] = useState(false);
  const [selectedGardenIdsForOptimization, setSelectedGardenIdsForOptimization] = useState(/** @type {string[]} */([]));
  const [selectedGardensWithOptimizations, setSelectedGardensWithOptimizations] = useState(/** @type {import("../types/global").Garden[]} */([]));
  const [modal, contextHolder] = useModal();
  const queryClient = useQueryClient();
  const [addGeoJsonProperty, setAddGeoJsonProperty] = useState(false);
  const [garden, setGarden] = useState(/** @type {import("../types/global").Garden | undefined} */(undefined));
  const navigate = useNavigate();
  /** @type {import("react").MutableRefObject<ReturnType<import("antd/lib/modal/confirm").ModalFunc> | null>} */
  const addGardenModalRef = useRef(null);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [bulkOptimizationValues, setBulkOptimizationValues] = useState(
    /** @type {Partial<Record<import("../types/global").OptimizationType, {optimizationName: import("../types/global").OptimizationType, isEnabled: boolean, value: number}>>} */({}),
  );
  const isGardensOptimizationsModalOpened = searchParameters.get("gardens_optimizations") === "true";
  const [selectedGardenIdsForWateringStatus, setSelectedGardenIdsForWateringStatus] = useState(
  /** @type {string[]} */([]),
  );
  const [bulkWateringStatus, setBulkWateringStatus] = useState(
  /** @type {import("../types/global").Garden["wateringStatus"]} */("active"),
  );
  const isWateringStatusModalOpened = searchParameters.get("batch_watering") === "true";

  /** @type {import("../types/global").addSelectedGardenWithOptimizationFunction} */
  const addSelectedGardenWithOptimization = useCallback((garden) => {
    setSelectedGardensWithOptimizations((prev) => {
      if (prev.some((selectedGarden) => String(selectedGarden.id) === String(garden.id))) return prev;

      return [...prev, garden];
    });
  }, []);

  /** @type {import("../types/global").removeSelectedGardenWithOptimizationFunction} */
  const removeSelectedGardenWithOptimization = useCallback((gardenId) => {
    setSelectedGardensWithOptimizations((prev) => prev.filter((garden) => String(garden.id) !== String(gardenId)));
  }, []);

  const resetBulkOptimizationModalState = useCallback(() => {
    setSelectedGardenIdsForOptimization([]);
    setSelectedGardensWithOptimizations([]);
    setBulkOptimizationValues({});
  }, []);

  const resetBulkWateringStatusState = useCallback(() => {
    setSelectedGardenIdsForWateringStatus([]);
    setBulkWateringStatus("active");
  }, []);

  const { mutateAsync: createGarden, isLoading: isCreatingGarden } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/garden").CreateGardenSuccessResponse>, unknown, import("../types/garden").CreateGardenRequest>} */ (
      useMutation((request) => axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/`, request), {
        onSuccess: (data) => {
          if (process.env.NODE_ENV === "development") {
            console.log("Garden created successfully:", data);
          }
          queryClient.invalidateQueries(["Gardens"]);
        },
      })
    );

  const { mutateAsync: mutateGarden, isLoading: isMutatingGarden } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<Extract<import("../types/global").GardenUpdateResponse, {success:true}>>, import("axios").AxiosResponse<Extract<import("../types/global").GardenUpdateResponse, {success: false}>>, import("../types/global").GardenUpdateRequest>} */ (
      useMutation((request) => axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden`, request), {
        onMutate: async (gardenUpdate) => {
          // Cancel any outgoing refetches to prevent race conditions
          await queryClient.cancelQueries(["Garden", gardenUpdate.id]);

          // Snapshot the previous value for rollback
          const previousGarden = queryClient.getQueryData(["Garden", gardenUpdate.id]);

          // Optimistically update the cache
          // @ts-ignore
          queryClient.setQueryData(["Garden", gardenUpdate.id], (old) => {
            if (!old?.data?.garden) return old;
            return {
              ...old,
              data: {
                ...old.data,
                garden: {
                  ...old.data.garden,
                  ...gardenUpdate,
                },
              },
            };
          });

          // @ts-ignore
          queryClient.setQueryData(["Gardens"], (old) => {
            if (!old) return old;

            // @ts-ignore
            return old.map((g) => (g.id === gardenUpdate.id ? { ...g, wateringStatus: gardenUpdate.wateringStatus } : g));
          });

          return { previousGarden, gardenId: gardenUpdate.id };
        },
        onError: (err, gardenUpdate, context) => {
          console.error("Garden update mutation failed:", err);

          // Rollback to previous state on error
          if (context?.previousGarden) {
            queryClient.setQueryData(["Garden", context.gardenId], context.previousGarden);
          }

          // Show error notification in development
          if (process.env.NODE_ENV === "development") {
            console.warn("Garden update mutation variables:", gardenUpdate);
          }
        },
        onSuccess: (data, variables) => {
          // Update cache with server response if available
          if (process.env.NODE_ENV === "development") {
            console.log("Garden update mutation successful:", data);
          }
        },
        onSettled: (data, error, variables) => {
          // Always invalidate to ensure consistency
          queryClient.invalidateQueries("Garden");
          queryClient.invalidateQueries("Gardens");
        },
      })
    );

  const { mutateAsync: removeGarden, isLoading: isRemovingGarden } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<Extract<import("../types/global").RemoveGardenResponse, {success:true}>>, import("axios").AxiosResponse<Extract<import("../types/global").RemoveGardenResponse, {success: false}>>, import("../types/global").RemoveGardenRequest>} */ (
      useMutation((request) => axios.delete(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${request.id}`), {
        onSuccess: async (data, variables) => {
          if (process.env.NODE_ENV === "development") {
            console.log("Garden removal successful:", data);
          }

          queryClient.invalidateQueries(["Gardens"]);
          notification.success({
            message: tr("Garden deleted successfully")
          });

          // Navigate away from the deleted garden
          navigate("/control-center/gardens");
        },
        onError: (err, variables) => {
          console.error("Garden removal failed:", err);
          if (err.response?.status === 403) {
            notification.error({
              message: tr("You are not authorized to delete this garden")
            });
          }
          else {
            notification.error({
              message: tr("Failed to delete the garden")
            });
          }
          if (process.env.NODE_ENV === "development") {
            console.warn("Garden removal variables:", variables);
          }
        },
        onSettled: (data, error, variables) => {
          // Invalidate both the specific garden query and all garden queries
          queryClient.invalidateQueries(["Garden", variables.id]);
          queryClient.invalidateQueries("Garden");
          queryClient.invalidateQueries("Gardens");
        },
      })
    );

  const { mutateAsync: optimizeManyGardens, isLoading: isOptimizingManyGardens } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").GardenOptimizeManyResponse>, unknown, import("../types/global").GardenOptimizeManyRequest>} */ (
      useMutation((request) => axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/optimize-many`, request), {
        onSuccess: async (data, variables) => {
          if (process.env.NODE_ENV === "development") {
            console.log("Bulk optimization successful:", data);
          }

          await Promise.all([
            queryClient.invalidateQueries(["Gardens"]),
            queryClient.invalidateQueries("Garden"),
            ...variables.gardenIds.map((id) => queryClient.invalidateQueries(["Garden", id])),
          ]);
        },
        onError: (err, variables) => {
          console.error("Bulk optimization failed:", err);
          if (process.env.NODE_ENV === "development") {
            console.warn("Bulk optimization variables:", variables);
          }
        },
        onSettled: (data, error, variables) => {
          queryClient.invalidateQueries(["Gardens"]);
          queryClient.invalidateQueries("Garden");
          variables?.gardenIds?.forEach((id) => {
            queryClient.invalidateQueries(["Garden", id]);
          });
        },
      })
    );

  const { mutateAsync: updateManyGardensWateringStatus, isLoading: isUpdatingManyGardensWateringStatus } =
  /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/global").GardenWateringStatusManyResponse>, unknown, import("../types/global").GardenWateringStatusManyRequest>} */ (
      useMutation((request) => axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/watering-status`, request), {
        onSuccess: (data, variables) => {
          // Patch the Gardens cache immediately so polygon colors / legends update
          // without waiting for the refetch.
          const updatedIds = new Set(variables.gardenIds.map(String));
          // @ts-ignore
          queryClient.setQueryData(["Gardens"], (old) => {
            if (!old) return old;
            // @ts-ignore
            return old.map((g) => (updatedIds.has(String(g.id)) ? { ...g, wateringStatus: variables.wateringStatus } : g));
          });
        },
        onError: (err, variables) => {
          console.error("Bulk watering status update failed:", err);
          if (process.env.NODE_ENV === "development") {
            console.warn("Bulk watering status variables:", variables);
          }
        },
        onSettled: (data, error, variables) => {
          queryClient.invalidateQueries(["Gardens"]);
          queryClient.invalidateQueries("Garden");
          variables?.gardenIds?.forEach((id) => {
            queryClient.invalidateQueries(["Garden", id]);
          });
        },
      })
    );

  const { data: gardenResponse, isFetching: isFetchingGardenResponse } =
    /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/global").GardenApiResponse>, unknown>} */ (
      useQuery(
        ["Garden", gardenId],
        () =>
          axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${gardenId}`, {
            params: {
              includeImages: true,
            },
          }),
        {
          enabled: !!gardenId,
          refetchOnWindowFocus: false,
        },
      )
    );

  const { data: gardens, isFetching: isFetchingGardens } = useQuery({
    queryKey: ["Gardens"],
    queryFn: () =>
      axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden`).then(
        (
          /** @type {import("axios").AxiosResponse<import("../types/global").GetGardensApiResponse>} */
          res,
        ) => res.data.gardens,
      ),
    enabled: !gardenId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const gardensWithOptimizations = (gardens || []).filter((garden) => {
      const optimization = garden.optimization;

      if (!optimization) return false;

      return Boolean(optimization.rain || optimization.sun || optimization.wind);
    });

    const nextSelectedGardensWithOptimizations = selectedGardenIdsForOptimization
      .map((selectedGardenIdForOptimization) => gardensWithOptimizations.find((garden) => String(garden.id) === String(selectedGardenIdForOptimization)))
      .filter(
        /**
         * @param {import("../types/global").Garden | undefined} garden
         * @returns {garden is import("../types/global").Garden}
         */
        (garden) => Boolean(garden),
      );

    setSelectedGardensWithOptimizations((prev) => {
      if (
        prev.length === nextSelectedGardensWithOptimizations.length &&
        prev.every((garden, index) => String(garden.id) === String(nextSelectedGardensWithOptimizations[index]?.id))
      ) {
        return prev;
      }

      return nextSelectedGardensWithOptimizations;
    });
  }, [gardens, selectedGardenIdsForOptimization]);

  const {
    data: dailyConsumption,
    isFetching: isFetchingDailyConsumption,
    refetch: refetchDailyConsumption,
  } = /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/global").ConsumptionPerAreaSuccessResponse, unknown>>} */ (
      useQuery(["DailyConsumption", gardenId], () => axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/consumption-by-area/${gardenId}`), {
        enabled: false,
        refetchOnWindowFocus: false,
      })
    );

  const {
    data: gardenDashboard,
    isFetching: isFetchingGardenDashboard,
    refetch: refetchGardenDashboard,
  } = /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/global").GardenDashboardResponse>>} */ (
      useQuery(
        ["GardenDashboard", gardenId],
        () =>
          axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/dashboard`, {
            id: process.env.REACT_APP_GARDEN_DASHBOARD,
            params: {
              gardenid: gardenId,
            },
          }),
        { enabled: false, refetchOnWindowFocus: false },
      )
    );

  const { mutateAsync: uploadGardenImages, isLoading: isUploadingGardenImages } =
    /** @type {import("react-query").UseMutationResult<import("axios").AxiosResponse<import("../types/garden").UploadGardenImagesResponse>,import("../types/garden").UploadGardenImagesRequest>} */ (
      useMutation(
        /**
         * @param {import("../types/global").UploadGardenImagesRequest} request
         * request.formData - FormData that contains files (field name(s) match backend: e.g. 'images' or 'image')
         * request.gardenId - id of the garden
         */
        (request) =>
          axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/${encodeURIComponent(request.gardenId)}/images`, request.formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }),
        {
          onMutate: async (variables) => {
            // Cancel outgoing refetches for this garden to avoid clobbering optimistic changes
            await queryClient.cancelQueries(["Garden", variables.gardenId]);

            // snapshot previous garden for rollback if needed
            const previousGarden = queryClient.getQueryData(["Garden", variables.gardenId]);

            // We *could* optimistically add placeholders to cache here, but for images
            // we'll simply rely on refetch/invalidation so uploaded images are loaded from server.
            return { previousGarden, gardenId: variables.gardenId };
          },
          onError: (err, variables, context) => {
            console.error("Garden image upload failed:", err);
            // rollback if we changed cache
            if (context?.previousGarden) {
              queryClient.setQueryData(["Garden", context.gardenId], context.previousGarden);
            }
          },
          onSuccess: (data, variables) => {
            console.log("Garden image upload success:", data);
          },
          onSettled: (data, error, variables) => {
            // Ensure the garden query is refreshed (and any lists)
            if (variables?.gardenId) {
              queryClient.invalidateQueries(["Garden", variables.gardenId]);
            }
            queryClient.invalidateQueries("Garden");
          },
        },
      )
    );

  /** @type {import("../types/global").updateIsOptimizationsPanelOpenedFunction} */
  const updateIsOptimizationsPanelOpened = useCallback((opened) => {
    setIsOptimizationsPanelOpened(opened);
  }, []);
  const [locale] = useState(localStorage.getItem("locale") || "tr");

  const openGardenDeleteConfirmModal = useCallback(() => {
    if (!gardenId) return;

    modal.confirm({
      content: <div>{tr("Are you sure you want to remove this garden?")}</div>,
      okText: tr("Delete"),
      okType: "danger",
      title: (
        <Typography.Title level={5} style={{ marginBottom: 0 }}>
          {tr("Delete Garden")}
        </Typography.Title>
      ),
      onOk: async () => {
        try {
          await removeGarden({ id: gardenId });
        } catch (error) {
          console.error("Failed to remove garden:", error);
        }
      },
    });
  }, [gardenId, modal, removeGarden]);

  /**
   * @param {number | undefined} val
   * @param {string | undefined} defaultUnit
   * @returns {number}
   */
  const convertToMetric = useCallback(
    (/** @type {number | undefined} */ val, /** @type {string | undefined} */ defaultUnit) => {
      if (locale === "en" && val !== undefined) {
        if (defaultUnit === "celsius") return Math.round(((val - 32) * 5) / 9);
        if (defaultUnit === "kilometer-per-hour") return Math.round(val * 1.60934);
      }
      return Math.round(val ?? 0); // ensures integer even if val is undefined/null
    },
    [locale],
  );

  const openGardenMapConfigModal = useCallback(() => {
    modal.confirm({
      bodyStyle: { padding: 0 },
      cancelButtonProps: { style: { display: "none" } },
      closable: true,
      content: (
        <DeviceProvider>
          <MapContainer center={[41, 29]} style={{ borderRadius: 12, height: 520 }} zoom={0} zoomControl={false}>
            <MapsProvider>
              <GardenProvider.EditGardenMaps />
            </MapsProvider>
          </MapContainer>
        </DeviceProvider>
      ),
      icon: null,
      maskClosable: false,
      okButtonProps: { style: { display: "none" } },
      title: (
        <Typography.Title level={5} style={{ marginBottom: 0 }}>
          {`${tr("Garden Maps Configuration")} ${garden?.name ? `: ${garden?.name}` : ""}`}
        </Typography.Title>
      ),
      width: "85vw",
    });
  }, [garden?.name, modal]);

  const openGardenReportsModal = useCallback(() => {
    modal.info({
      bodyStyle: { padding: 0 },
      cancelButtonProps: { style: { display: "none" } },
      closable: true,
      content: <GardenProvider.GardenReports />,
      icon: null,
      maskClosable: true,
      okButtonProps: { style: { display: "none" } },
      title: (
        <Typography.Title level={5} style={{ marginBottom: 0 }}>
          {`${tr("Garden Report")} ${garden?.name ? `: ${garden?.name}` : ""}`}
        </Typography.Title>
      ),
      width: "85vw",
    });
  }, [garden?.name, modal]);

  const openAddGardenModal = useCallback(
    () =>
      modal.confirm({
        bodyStyle: { padding: 0 },
        cancelButtonProps: { style: { display: "none" } },
        closable: true,
        content: <GardenProvider.AddGarden />,
        icon: null,
        maskClosable: true,
        onCancel: (e) => {
          setSearchParameters((prev) => {
            prev.delete("add_garden");
            return prev;
          });
        },
        onOk: (e) => {
          console.log(e);
        },
        title: (
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            {tr("Add Garden")}
          </Typography.Title>
        ),
        width: "85vw",
        zIndex: 1051,
      }),
    [modal, setSearchParameters],
  );

  const openGardenSettingsModal = useCallback(
    (/** @type {import("../types/global").Garden} */ garden) => {
      console.count("openGardenSettingsModal called");

      modal.info({
        content: <GardenProvider.GardenDetails />,
        icon: null,
        maskClosable: true,
        okText: tr("Close"),
        title: <GardenProvider.GardenTitleInDetailsModal />,
      });

      console.count("openGardenSettingsModal rendered");
    },
    [modal],
  );

  const closeGardensOptimizationsModal = useCallback(() => {
    resetBulkOptimizationModalState();
    setSearchParameters((prev) => {
      prev.delete("gardens_optimizations");
      return prev;
    });
  }, [resetBulkOptimizationModalState, setSearchParameters]);

  const openGardensOptimizationsModal = useCallback(() => {
    setSearchParameters((prev) => {
      prev.set("gardens_optimizations", "true");
      return prev;
    });
  }, [setSearchParameters]);

  const openGardensOptimizationsReviewModal = useCallback(() => {
    const normalizedOptimizePayload = {
      sun: Boolean(bulkOptimizationValues.sun?.isEnabled),
      rain: Boolean(bulkOptimizationValues.rain?.isEnabled),
      wind: Boolean(bulkOptimizationValues.wind?.isEnabled),
      active: Object.values(bulkOptimizationValues).some((optimization) => optimization?.isEnabled),
      limits: {
        max: {
          sun: convertToMetric(bulkOptimizationValues.sun?.value, "celsius") ?? 0,
          rain: bulkOptimizationValues.rain?.value ?? 0,
          wind: convertToMetric(bulkOptimizationValues.wind?.value, "kilometer-per-hour") ?? 0,
        },
      },
    };

    const bulkOptimizationPayload = {
      gardenIds: selectedGardenIdsForOptimization,
      optimize: normalizedOptimizePayload,
    };

    const selectedGardens = selectedGardenIdsForOptimization
      .map((selectedGardenId) => (gardens || []).find((garden) => String(garden.id) === String(selectedGardenId)))
      .map((garden, index) => ({
        key: String(index),
        name: garden?.name || `${tr("Garden")} #${selectedGardenIdsForOptimization[index]}`,
      }));

    const optimizationRows = Object.keys(bulkOptimizationValues).map((optimizationName) => {
      const optimization = bulkOptimizationValues[/** @type {keyof typeof bulkOptimizationValues} */ (optimizationName)];

      return {
        key: optimizationName,
        optimization: tr(optimizationName.charAt(0).toUpperCase() + optimizationName.slice(1)),
        status: optimization ? (optimization.isEnabled ? tr("Enabled") : tr("Disabled")) : tr("Not configured"),
        value: optimization ? optimization.value : "-",
      };
    });

    modal.confirm({
      cancelText: tr("Go Back"),
      content: (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Text>{tr("Please review the selected gardens and optimization settings before continuing.")}</Typography.Text>

          <Typography.Text strong>{tr("Gardens to be affected")}</Typography.Text>
          <Table columns={[{ dataIndex: "name", key: "name", title: tr("Garden Name") }]} dataSource={selectedGardens} pagination={false} size="small" />

          <Typography.Text strong>{tr("Details")}</Typography.Text>
          <Table
            columns={[
              {
                dataIndex: "optimization",
                key: "optimization",
                title: tr("Optimization"),
              },
              { dataIndex: "status", key: "status", title: tr("Status") },
              { dataIndex: "value", key: "value", title: tr("Value") },
            ]}
            dataSource={optimizationRows}
            pagination={false}
            size="small"
          />
        </Space>
      ),
      okText: tr("Save Optimizations"),
      onCancel: () => { },
      onOk: async () => {
        try {
          const { data } = await optimizeManyGardens(bulkOptimizationPayload);

          if (data.success !== true) {
            throw new Error(data.message || data.error || tr("Bulk optimization failed"));
          }

          notification.success({
            message: tr("Optimizations saved successfully"),
            placement: "topRight",
          });

          closeGardensOptimizationsModal();
        } catch (error) {
          notification.error({
            message: error instanceof Error ? error.message : tr("Failed to save optimizations"),
            placement: "topRight",
          });
          throw error;
        }
      },
      title: tr("Are you sure?"),
      zIndex: 1053,
    });
  }, [bulkOptimizationValues, closeGardensOptimizationsModal, convertToMetric, gardens, modal, optimizeManyGardens, selectedGardenIdsForOptimization]);

  const gardensOptimizationsModalFooter = useMemo(
    () => [
      <Button key="close" onClick={closeGardensOptimizationsModal}>
        {tr("Close")}
      </Button>,
      <Button key="save" type="primary" disabled={selectedGardenIdsForOptimization.length === 0} onClick={openGardensOptimizationsReviewModal}>
        {tr("Save")}
      </Button>,
    ],
    [closeGardensOptimizationsModal, openGardensOptimizationsReviewModal, selectedGardenIdsForOptimization.length],
  );

  const closeWateringStatusModal = useCallback(() => {
    resetBulkWateringStatusState();
    setSearchParameters((prev) => {
      prev.delete("batch_watering");
      return prev;
    });
  }, [resetBulkWateringStatusState, setSearchParameters]);

  const openWateringStatusModal = useCallback(() => {
    setSearchParameters((prev) => {
      prev.set("batch_watering", "true");
      return prev;
    });
  }, [setSearchParameters]);

  const openWateringStatusReviewModal = useCallback(() => {
    const isActivating = bulkWateringStatus === "active";
    const selectedGardens = selectedGardenIdsForWateringStatus.map((selectedGardenId, index) => {
      const garden = (gardens || []).find((g) => String(g.id) === String(selectedGardenId));
      return {
        key: String(index),
        name: garden?.name || `${tr("Garden")} #${selectedGardenId}`,
        current: garden?.wateringStatus === "active" ? tr("Active") : tr("Passive"),
      };
    });
    // Gardens already in the target state are a no-op on the server, but the operator
    // should see that before confirming.
    const unchangedCount = selectedGardens.filter(
      (g) => g.current === (isActivating ? tr("Active") : tr("Passive")),
    ).length;

    modal.confirm({
      cancelText: tr("Go Back"),
      content: (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Text>
            {isActivating
              ? tr("Watering will be enabled for the gardens below.")
              : tr("Watering will be disabled for the gardens below.")}
          </Typography.Text>
          <Table
            columns={[
              { dataIndex: "name", key: "name", title: tr("Garden Name") },
              { dataIndex: "current", key: "current", title: tr("Current Status") },
            ]}
            dataSource={selectedGardens}
            pagination={false}
            scroll={{ y: 240 }}
            size="small"
          />
          {unchangedCount > 0 && (
            <Alert
              message={`${unchangedCount} ${tr("of the selected gardens are already in this state and will not change.")}`}
              type="info"
              showIcon
            />
          )}
        </Space>
      ),
      okButtonProps: { danger: !isActivating },
      okText: isActivating ? tr("Enable Watering") : tr("Disable Watering"),
      onOk: async () => {
        try {
          const { data } = await updateManyGardensWateringStatus({
            gardenIds: selectedGardenIdsForWateringStatus,
            wateringStatus: bulkWateringStatus,
          });
          if (data.success !== true) {
            throw new Error(data.message || data.error || tr("Bulk watering status update failed"));
          }
          if (data.notFound?.length) {
            notification.warning({
              message: `${data.notFound.length} ${tr("gardens could not be updated")}`,
              placement: "topRight",
            });
          }
          if (data.failedDevices?.length) {
            notification.warning({
              message: `${data.failedDevices.length} ${tr("devices could not be notified")}`,
              placement: "topRight",
            });
          }
          notification.success({
            message: tr("Watering status updated successfully"),
            placement: "topRight",
          });
          closeWateringStatusModal();
        } catch (error) {
          notification.error({
            message: error instanceof Error ? error.message : tr("Failed to update watering status"),
            placement: "topRight",
          });
          throw error; // keeps the confirm modal open
        }
      },
      title: tr("Are you sure?"),
      zIndex: 1053,
    });
  }, [
    bulkWateringStatus,
    closeWateringStatusModal,
    gardens,
    modal,
    selectedGardenIdsForWateringStatus,
    updateManyGardensWateringStatus,
  ]);

  const wateringStatusModalFooter = useMemo(
    () => [
      <Button key="close" onClick={closeWateringStatusModal}>
        {tr("Close")}
      </Button>,
      <Button
        key="apply"
        type="primary"
        danger={bulkWateringStatus !== "active"}
        disabled={selectedGardenIdsForWateringStatus.length === 0}
        loading={isUpdatingManyGardensWateringStatus}
        onClick={openWateringStatusReviewModal}
      >
        {tr("Apply")}
      </Button>,
    ],
    [
      bulkWateringStatus,
      closeWateringStatusModal,
      isUpdatingManyGardensWateringStatus,
      openWateringStatusReviewModal,
      selectedGardenIdsForWateringStatus.length,
    ],
  );

  useEffect(() => {
    /**
     * @param {Event} event
     */
    const handleBulkOptimizationChange = (event) => {
      const customEvent = /** @type {CustomEvent<{optimizationName?: unknown, isEnabled?: unknown, value?: unknown}>} */ (event);
      const detail = customEvent.detail;

      if (!detail || typeof detail !== "object") return;

      const optimizationName = detail.optimizationName;
      const isEnabled = detail.isEnabled;
      const value = detail.value;

      if (optimizationName !== "rain" && optimizationName !== "sun" && optimizationName !== "wind") return;
      if (typeof isEnabled !== "boolean") return;
      if (typeof value !== "number") return;

      setBulkOptimizationValues((prev) => ({
        ...prev,
        [optimizationName]: {
          optimizationName,
          isEnabled,
          value,
        },
      }));
    };

    window.addEventListener(GARDEN_OPTIMIZATION_CHANGE_EVENT, handleBulkOptimizationChange);

    return () => {
      window.removeEventListener(GARDEN_OPTIMIZATION_CHANGE_EVENT, handleBulkOptimizationChange);
    };
  }, []);

  /**
   * @typedef {Object} CustomUploadOptions
   * @property {File} file
   * @property {(xhr: any, file: any) => void} [onSuccess]
   * @property {(err: Error, file?: any) => void} [onError]
   */

  /**
   * @param {CustomUploadOptions} options
   */
  const handleUpload = useCallback(
    async (options) => {
      const { file, onSuccess, onError } = options;

      if (!file) return;

      const isImage = file.type?.startsWith("image/");
      if (!isImage) {
        notification.error({
          message: "Only image files are allowed.",
          placement: "topRight",
        });
        onError?.(new Error("Not an image"));
        return;
      }

      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        notification.error({
          message: "Image must be smaller than 10MB.",
          placement: "topRight",
        });
        onError?.(new Error("Too big"));
        return;
      }

      if (!garden?.id) {
        notification.error({
          message: "Garden not available",
          placement: "topRight",
        });
        onError?.(new Error("No garden id"));
        return;
      }

      try {
        const formData = new FormData();
        formData.append("image", file);

        await uploadGardenImages({ gardenId: garden.id, formData });

        notification.success({
          message: "Image uploaded successfully.",
          placement: "topRight",
        });
        queryClient.invalidateQueries(["Garden", garden.id]);

        // Pass null as xhr, second param is RcFile-like object with uid & status
        onSuccess?.(null, { ...file, uid: Date.now() + "", status: "done" });
      } catch (err) {
        notification.error({
          message: "Upload failed.",
          placement: "topRight",
        });
        onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    },
    [garden?.id, uploadGardenImages, queryClient],
  );

  const gardenContextValue = useMemo(() => {
    setGarden(gardenResponse?.data.garden);

    /** @type {import("../types/global").GardenContextValue} */
    const value = {
      addGeoJsonPropertyState: [addGeoJsonProperty, setAddGeoJsonProperty],
      addSelectedGardenWithOptimization,
      dailyConsumption: dailyConsumption?.data.consumptionPerArea,
      garden: gardenResponse?.data?.garden,
      gardens, // Expose all gardens
      gardenDashboard: gardenDashboard?.data,
      gardenId,
      isFetchingGardenResponse,
      isFetchingGardens, // Expose fetching state for all gardens
      isFetchingDailyConsumption,
      isFetchingGardenDashboard,
      isCreatingGarden,
      isMutatingGarden,
      isOptimizingManyGardens,
      isRemovingGarden,
      isOptimizationsPanelOpened,
      selectedGardenIdsForOptimization,
      selectedGardensWithOptimizations,
      bulkWateringStatusState: [bulkWateringStatus, setBulkWateringStatus],
      selectedGardenIdsForWateringStatus,
      isUpdatingManyGardensWateringStatus,
      setSelectedGardenIdsForOptimization,
      isUploadingGardenImages,
      createGarden,
      mutateGarden,
      optimizeManyGardens,
      removeGarden,
      openGardenDeleteConfirmModal,
      openGardenMapConfigModal,
      openGardenReportsModal,
      openAddGardenModal,
      openGardenSettingsModal,
      openGardensOptimizationsModal,
      removeSelectedGardenWithOptimization,
      resetBulkOptimizationModalState,
      refetchDailyConsumption,
      refetchGardenDashboard,
      updateIsOptimizationsPanelOpened,
      uploadGardenImages,
      handleUpload,
      setSelectedGardenIdsForWateringStatus,
      updateManyGardensWateringStatus,
      openWateringStatusModal,
      closeWateringStatusModal,
      resetBulkWateringStatusState,
    };

    return value;
  }, [
    addGeoJsonProperty,
    addSelectedGardenWithOptimization,
    dailyConsumption?.data.consumptionPerArea,
    gardenDashboard?.data,
    gardenId,
    gardenResponse?.data.garden,
    gardens,
    isFetchingDailyConsumption,
    isFetchingGardenDashboard,
    isFetchingGardenResponse,
    isFetchingGardens,
    isCreatingGarden,
    isMutatingGarden,
    isOptimizingManyGardens,
    isRemovingGarden,
    isOptimizationsPanelOpened,
    selectedGardenIdsForOptimization,
    selectedGardensWithOptimizations,
    bulkWateringStatus,
    selectedGardenIdsForWateringStatus,
    isUpdatingManyGardensWateringStatus,
    createGarden,
    mutateGarden,
    optimizeManyGardens,
    removeGarden,
    openGardenDeleteConfirmModal,
    openGardenMapConfigModal,
    openGardenReportsModal,
    openAddGardenModal,
    openGardenSettingsModal,
    openGardensOptimizationsModal,
    removeSelectedGardenWithOptimization,
    resetBulkOptimizationModalState,
    refetchDailyConsumption,
    refetchGardenDashboard,
    updateIsOptimizationsPanelOpened,
    isUploadingGardenImages,
    uploadGardenImages,
    handleUpload,
    updateManyGardensWateringStatus,
    openWateringStatusModal,
    closeWateringStatusModal,
    resetBulkWateringStatusState,
  ]);

  if (process.env.NODE_ENV === "development") {
    window.garden = gardenResponse?.data.garden;
  }

  useEffect(() => {
    console.count("GardenProvider render");
    const addGardenForm = searchParameters.get("add_garden");

    if (addGardenForm === "true") {
      addGardenModalRef.current = openAddGardenModal();
    }

    if (addGardenForm !== "true" && !!addGardenModalRef.current) {
      addGardenModalRef.current.destroy();
      addGardenModalRef.current = null;
    }
  }, [openAddGardenModal, searchParameters]);

  return (
    <GardenContext.Provider value={gardenContextValue}>
      {children}
      {contextHolder}
      <Modal
        closable={false}
        destroyOnClose
        footer={gardensOptimizationsModalFooter}
        onCancel={closeGardensOptimizationsModal}
        open={isGardensOptimizationsModalOpened}
        style={{ maxWidth: "1100px" }}
        title={
          <Space>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {tr("Optimization Settings")}
            </Typography.Title>
          </Space>
        }
        width="85vw"
        zIndex={1052}
      >
        <GardenProvider.OptimizationsModal />
      </Modal>
      <Modal
        closable={false}
        destroyOnClose
        footer={wateringStatusModalFooter}
        onCancel={closeWateringStatusModal}
        open={isWateringStatusModalOpened}
        style={{ maxWidth: "1100px" }}
        title={
          <Space>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {tr("Toggle Watering Status")}
            </Typography.Title>
          </Space>
        }
        width="85vw"
        zIndex={1052}
      >
        <GardenProvider.WateringStatusModal />
      </Modal>
    </GardenContext.Provider>
  );
}

GardenProvider.AddGarden = function GardenProviderAddGarden() {
  const { createGarden, isCreatingGarden, gardens } = useGarden();
  const [newGardenName, setNewGardenName] = useState(/** @type {string} */(""));
  const [geoJson, setGeoJson] = useState(/** @type {import("geojson").Feature | null} */(null));
  const [, setSearchParameters] = useSearchParams();

  /** @type {{ center: import("leaflet").LatLngTuple, zoom: number }} */
  const { center, zoom } = useMemo(() => {
    const defaultResult = { center: /** @type {import("leaflet").LatLngTuple} */ ([30.293406, -97.735364]), zoom: 10 };

    if (!gardens || gardens.length === 0) return defaultResult;

    const allFeatures = gardens
      .map((g) => g.geoJson)
      .filter((f) => f && f.type === "Feature" && f.geometry && f.geometry.coordinates);

    if (allFeatures.length > 0) {
      const featureCollection = turf.featureCollection(allFeatures);
      const centerPoint = turf.center(featureCollection);
      const [minLng, minLat, maxLng, maxLat] = turf.bbox(featureCollection);

      if (centerPoint?.geometry?.coordinates) {
        const [lng, lat] = centerPoint.geometry.coordinates;

        // Calculate the max span in degrees across both axes
        const lngSpan = maxLng - minLng;
        const latSpan = maxLat - minLat;
        const maxSpan = Math.max(lngSpan, latSpan);

        // Map degree span to a Leaflet zoom level
        const zoom = maxSpan <= 0
          ? 14
          : Math.max(1, Math.min(14, Math.round(Math.log2(360 / maxSpan)) + 2));

        return { center: [lat, lng], zoom };
      }
    }

    // Fallback: first garden's device location
    const firstGardenWithDeviceLocation = gardens.find(
      (g) => g.devices?.[0]?.location?.lat && g.devices?.[0]?.location?.lng
    );
    const firstDeviceLocation = firstGardenWithDeviceLocation?.devices?.[0]?.location;

    if (firstDeviceLocation?.lat !== undefined && firstDeviceLocation?.lng !== undefined) {
      return { center: [firstDeviceLocation.lat, firstDeviceLocation.lng], zoom: 14 };
    }

    return defaultResult;
  }, [gardens]);

  const onGardenCreate = useCallback(async () => {
    if (!newGardenName) {
      alert(tr("Please enter a garden name."));
      return;
    }

    if (!geoJson) {
      alert(tr("Please draw the garden area on the map."));
      return;
    }

    try {
      const { data: createGardenSuccessResponse } = await createGarden({
        name: newGardenName.trim(),
        geoJson,
      });

      if (createGardenSuccessResponse.success !== true) throw new Error(tr("Garden creation failed"));

      notification.success({
        message: tr("Garden created successfully"),
      });

      setSearchParameters((prev) => {
        prev.delete("add_garden");
        prev.set("selectedGardenId", createGardenSuccessResponse.garden.id);
        return prev;
      });
    } catch (error) {
      notification.error({
        message: tr("Failed to create garden"),
      });
    }
  }, [createGarden, geoJson, newGardenName, setSearchParameters]);

  return (
    <Row gutter={[0, 16]}>
      <Col span={24}>
        <Space>
          <Input
            allowClear
            value={newGardenName}
            onChange={(e) => {
              setNewGardenName(e.target.value);
            }}
            placeholder={tr("Park Name")}
            style={{ borderRadius: 5 }}
            className="w-60"
          />
          <Button type="primary" onClick={onGardenCreate} loading={isCreatingGarden}>
            {tr("Save")}
          </Button>
        </Space>
      </Col>

      <Col span={24}>
        <MapContainer center={center} style={{ borderRadius: 12, height: 520 }} zoom={zoom} zoomControl={false}>
          <MapsProvider>
            <MapsProvider.Container>
              <MapsProvider.AddGardenArea callback={setGeoJson} />
            </MapsProvider.Container>
          </MapsProvider>
        </MapContainer>
      </Col>
    </Row>
  );
};

GardenProvider.EditGardenMaps = function GardenProviderEditParkMaps() {
  const { garden } = useGarden();
  const map = useMap();
  const { fitBoundsToGardens } = useMaps();

  useEffect(() => {
    if (!map) return;
    if (!garden) return;

    map.whenReady((e) => {
      const m = e.target;

      fitBoundsToGardens({ gardens: [garden], map: m });
    });
  }, [fitBoundsToGardens, garden, map]);

  if (!garden) return null;

  return (
    <MapsProvider.Container>
      <MapsProvider.GardenLayersControl>
        <MapsProvider.GardenAreas editable />
        <MapsProvider.GardenDevices checked={false} />
      </MapsProvider.GardenLayersControl>
    </MapsProvider.Container>
  );
};

GardenProvider.DevicesManagement = function GardenProviderDevicesManagement() {
  return <>manage_devices</>;
};

GardenProvider.GardenMenu = function GardenProviderGardenMenu() {
  const { garden, gardenId, openGardenDeleteConfirmModal, openGardenMapConfigModal, openGardenReportsModal, openGardenSettingsModal } = useGarden();
  const navigate = useNavigate();

  if (!garden) return null;
  if (!gardenId) return null;

  return (
    <Dropdown
      overlay={
        <Menu key={"garden-menu"}>
          <Menu.Item icon={<InfoCircleOutlined />} key={`${garden.id}-gardenMenuItem1`} onClick={() => openGardenSettingsModal(garden)}>
            {tr("Garden Details")}
          </Menu.Item>

          <Menu.Item icon={<IoMapOutline />} key={`${garden.id}-gardenMenuItem5`} onClick={openGardenMapConfigModal}>
            {tr("Edit Garden Map")}
          </Menu.Item>

          <Menu.Divider />

          <Menu.Item icon={<BarChartOutlined />} key={`${garden.id}-gardenMenuItem2`} onClick={openGardenReportsModal}>
            {tr("Garden Report")}
          </Menu.Item>

          <Menu.Item
            icon={<CalendarOutlined />}
            key={`${garden.id}-gardenMenuItem3`}
            onClick={() =>
              navigate("/control-center/work-orders", {
                state: { initGardenId: gardenId },
              })
            }
          >
            <Space>
              {tr("Work Orders History")}

              <Tooltip placement="topRight" title={tr("Redirects to Work Orders page")}>
                <span>
                  <RxOpenInNewWindow />
                </span>
              </Tooltip>
            </Space>
          </Menu.Item>

          <Menu.Divider />

          <Menu.Item danger key={`${garden.id}-gardenMenuItem6`} icon={<DeleteOutlined />} onClick={openGardenDeleteConfirmModal}>
            {tr("Delete Garden")}
          </Menu.Item>
        </Menu>
      }
      trigger={["click"]}
    >
      <Button icon={<MoreOutlined />} />
    </Dropdown>
  );
};

GardenProvider.GeoJSONAddPropertyForm = function GardenProviderGeoJSONAddPropertyForm() {
  const { addGeoJsonPropertyState, garden, isMutatingGarden, mutateGarden } = useGarden();
  const [addGeoJsonProperty] = addGeoJsonPropertyState;
  const formName = "geojson-add-property-form";

  if (!garden) return null;

  return !addGeoJsonProperty ? null : (
    <Form.Provider
      onFormFinish={(name, info) => {
        if (name === formName) {
          info.forms[formName].resetFields();
        }
      }}
    >
      <Form
        layout="inline"
        name={formName}
        onFinish={async (values) => {
          console.log(values, isMutatingGarden, mutateGarden);
          const { properties: _properties } = garden.geoJson;
          /** @type {import("geojson").Feature["properties"]} */
          const updatedProperties = {
            ..._properties,
            [values.geoJsonPropertyKey]: values.geoJsonPropertyValue,
          };

          /** @type {import("../types/global").GardenUpdateRequest} */
          const _gardenRequest = {
            geoJson: {
              ...garden.geoJson,
              properties: updatedProperties,
            },
            id: garden.id,
          };

          const response = await mutateGarden(_gardenRequest);
          console.log("GeoJSON property added:", response);
        }}
        onFinishFailed={(errorInfo) => console.log(errorInfo)}
        size="small"
      >
        <Row gutter={[8, 8]}>
          <Col span={9}>
            <Form.Item name="geoJsonPropertyKey" rules={[{ required: true, message: tr("Set proporty key") }]} style={{ margin: 0 }}>
              <Input disabled={isMutatingGarden} placeholder={tr("Property Key")} />
            </Form.Item>
          </Col>

          <Col span={9}>
            <Form.Item name="geoJsonPropertyValue" rules={[{ required: true, message: tr("Set proporty value") }]} style={{ margin: 0 }}>
              <Input disabled={isMutatingGarden} placeholder={tr("Property Value")} />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item style={{ margin: 0 }}>
              <Button disabled={isMutatingGarden} htmlType="submit" style={{ width: "100%" }} type="primary">
                {tr("Add")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Form.Provider>
  );
};

/**
 * @type {import("react").FC<import("react").PropsWithChildren<unknown>>}
 */
GardenProvider.PageHeader = function GardenProviderPageHeader({ children }) {
  const navigate = useNavigate();
  const { garden } = useGarden();

  return (
    <PageHeader
      style={{ height: "100%" }}
      extra={[<GardenProvider.StatusSwitch key={1} />, <GardenProvider.GardenMenu key={2} />]}
      ghost={false}
      onBack={() => navigate(-1)}
      title={garden?.name || tr("Garden")}
    >
      {children}
    </PageHeader>
  );
};

/**
 * @returns {import("react").ReactElement | null}
 */
GardenProvider.GardenDetails = function GardenProviderGardenDetails() {
  const { addGeoJsonPropertyState, dailyConsumption, garden, mutateGarden, refetchDailyConsumption, handleUpload } = useGarden();
  const [addGeoJsonProperty, setAddGeoJsonProperty] = addGeoJsonPropertyState;

  useEffect(() => {
    refetchDailyConsumption();
  }, [refetchDailyConsumption]);

  // @ts-ignore
  const renderPropertyValue = (value) => {
    if (value == null) return tr("Not Set");

    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }

    if (Array.isArray(value)) {
      // empty array like [{}]
      if (value.length === 0) return tr("Not Set");

      // array of objects
      return JSON.stringify(value);
    }

    if (typeof value === "object") {
      // empty object {}
      if (Object.keys(value).length === 0) return tr("Not Set");

      return JSON.stringify(value);
    }

    return tr("Not Set");
  };

  if (!garden) return null;

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <>
        {!!garden?.imageUrls?.length ? (
          <>
            {/* Image row */}
            <Row gutter={[0, 8]}>
              <Col span={24}>
                <Image
                  src={garden?.imageUrls[0].startsWith("http") ? garden.imageUrls[0] : `${window.location.origin}${garden.imageUrls[0]}`}
                  width="100%"
                  height={160}
                  preview={{
                    mask: tr("View Image"),
                    maskStyle: { fontSize: 14 },
                  }}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                  fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='240' height='160'%3E%3Crect width='240' height='160' fill='%23ddd'/%3E%3C/svg%3E"
                />
              </Col>
            </Row>

            {/* Upload button row */}
            <Row gutter={[0, 8]}>
              <Col span={24}>
                <Upload
                  accept="image/*"
                  maxCount={1}
                  showUploadList={false}
                  customRequest={async (info) => {
                    try {
                      if (!(info.file instanceof File)) {
                        info.onError?.(new Error("Invalid file"));
                        return;
                      }
                      await handleUpload(info);
                    } catch (err) {
                      info.onError?.(err instanceof Error ? err : new Error(String(err)), info.file);
                    }
                  }}
                >
                  <Button block icon={<UploadOutlined />}>
                    {tr("Upload Garden Image")}
                  </Button>
                </Upload>
              </Col>
            </Row>
          </>
        ) : (
          // Only upload button if no image
          <Row gutter={[0, 8]}>
            <Col span={24}>
              <Upload
                accept="image/*"
                maxCount={1}
                showUploadList={false}
                customRequest={async (info) => {
                  try {
                    if (!(info.file instanceof File)) {
                      info.onError?.(new Error("Invalid file"));
                      return;
                    }
                    await handleUpload(info);
                  } catch (err) {
                    info.onError?.(err instanceof Error ? err : new Error(String(err)), info.file);
                  }
                }}
              >
                <Button block icon={<UploadOutlined />}>
                  {tr("Upload Garden Image")}
                </Button>
              </Upload>
            </Col>
          </Row>
        )}
      </>

      <Descriptions bordered column={1} size="small">
        <Descriptions.Item
          label={
            <>
              {tr("Daily Consumption")} / m<sup>2</sup>
            </>
          }
        >
          <Typography.Text>{dailyConsumption}</Typography.Text>
        </Descriptions.Item>

        {
          <Descriptions.Item
            label={
              <>
                {tr("Green Area")} m<sup>2</sup>
              </>
            }
          >
            <Typography.Text
              editable={{
                icon: <EditOutlined style={{ transform: "translate(0, -2px)" }} />,
                onChange: /** @type {(greenArea:string) => Promise<void>} - onChange is triggered onBlur */ (
                  async (greenArea) => {
                    /** @type {import("../types/global").GardenUpdateRequest} */
                    const _gardenRequest = {
                      id: garden.id,
                      greenArea: Number(greenArea),
                    };

                    await mutateGarden(_gardenRequest);
                  }
                ),
                tooltip: tr("Edit Green Area"),
              }}
              style={{ margin: 0, textWrap: "nowrap" }}
            >
              {garden.greenArea?.toString() || ""}
            </Typography.Text>
          </Descriptions.Item>
        }
      </Descriptions>

      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Space>
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            {tr("Custom Values")}
          </Typography.Title>

          {addGeoJsonProperty ? (
            <Tooltip title={tr("Cancel adding new property")}>
              <span>
                <Button danger icon={<CloseCircleOutlined />} onClick={() => setAddGeoJsonProperty(false)} size="small" type="text" />
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={tr("Add new property to GeoJSON properties")}>
              <span>
                <Button icon={<PlusCircleOutlined />} onClick={() => setAddGeoJsonProperty(true)} size="small" style={{ color: "#3F7C88" }} type="text" />
              </span>
            </Tooltip>
          )}
        </Space>

        <GardenProvider.GeoJSONAddPropertyForm />

        <Descriptions bordered column={1} size="small">
          {!Object.keys(garden.geoJson.properties || {}).filter((p) => p !== "MAHAL_ID" && p !== "MAHAL_ADI").length && (
            <Descriptions.Item>
              <Empty description={tr("No custom properties added yet.")} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Descriptions.Item>
          )}

          {Object.keys(garden.geoJson.properties || {})
            .filter((p) => p !== "MAHAL_ID" && p !== "MAHAL_ADI" && p !== "collectedProperties")
            // sort reversely alphabetically
            .sort((a, b) => b.localeCompare(a))
            .map(
              /** @type {import("../types/global").geoJsonPropertiesObjectKeyMapHandlerFunction} */(
                (key, index) => (
                  <Descriptions.Item key={`${key.toString()}_${index}`} label={key} labelStyle={{ width: "50%" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                      }}
                    >
                      <Typography.Text style={{ margin: 0 }}>{renderPropertyValue((garden.geoJson.properties || {})[/** @type {string} */ (key)])}</Typography.Text>

                      {key !== "MAHAL_ID" && key !== "MAHAL_ADI" && (
                        <Tooltip title={tr("Delete property")}>
                          <span>
                            <Button
                              icon={<DeleteOutlined style={{ color: "#3F7C88" }} />}
                              onClick={() => {
                                if (!garden.geoJson.properties) return;

                                const _properties = Object.keys(garden.geoJson.properties)
                                  .filter((k) => k !== key)
                                  .reduce((obj, /** @type {string} */ k) => {
                                    obj[k] = (garden.geoJson.properties || {})[k];
                                    return obj;
                                  }, /** @type {Record<string, unknown>} */({}));

                                /** @type {import("../types/global").GardenUpdateRequest} */
                                const _gardenRequest = {
                                  geoJson: {
                                    ...garden.geoJson,
                                    properties: _properties,
                                  },
                                  id: garden.id,
                                };

                                mutateGarden(_gardenRequest);
                              }}
                              size="small"
                              type="text"
                            />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </Descriptions.Item>
                )
              ),
            )}
        </Descriptions>
      </Space>
    </Space>
  );
};

GardenProvider.GardenReports = function GardenProviderGardenReports() {
  const { garden, gardenDashboard, isFetchingGardenDashboard, refetchGardenDashboard } = useGarden();

  useEffect(() => {
    refetchGardenDashboard();
  }, [refetchGardenDashboard]);

  if (!garden) return null;

  return (
    <Skeleton loading={isFetchingGardenDashboard} active>
      {gardenDashboard?.success ? (
        <iframe allowTransparency frameBorder={0} height="500px" src={gardenDashboard.url} title={`${tr("Garden Report")}: ${garden.name}`} width="100%" />
      ) : (
        <Typography.Text type="danger">{tr("Failed to load garden report.")}</Typography.Text>
      )}
    </Skeleton>
  );
};

GardenProvider.OptimizationsModal = function GardenProviderOptimizationsModal() {
  const {
    addSelectedGardenWithOptimization,
    gardens,
    optimizeManyGardens,
    removeSelectedGardenWithOptimization,
    resetBulkOptimizationModalState,
    selectedGardenIdsForOptimization,
    selectedGardensWithOptimizations,
    setSelectedGardenIdsForOptimization,
  } = useGarden();
  const [, setSearchParameters] = useSearchParams();
  const { width } = useWindowSize();
  const gardensWithOptimizations = useMemo(
    () =>
      (gardens || []).filter((garden) => {
        const optimization = garden.optimization;
        if (!optimization) return false;

        return Boolean(optimization.rain || optimization.sun || optimization.wind);
      }),
    [gardens],
  );

  const handleSelectedGardenIdsForOptimizationChange = useCallback(
    (nextTargetKeys) => {
      setSelectedGardenIdsForOptimization(nextTargetKeys);

      const nextSelectedGardenIds = new Set(nextTargetKeys.map(String));
      const selectedGardensWithOptimizationIds = new Set(selectedGardensWithOptimizations.map((garden) => String(garden.id)));

      selectedGardensWithOptimizations
        .filter((garden) => !nextSelectedGardenIds.has(String(garden.id)))
        .forEach((garden) => {
          removeSelectedGardenWithOptimization(garden.id);
        });

      gardensWithOptimizations
        .filter((garden) => nextSelectedGardenIds.has(String(garden.id)) && !selectedGardensWithOptimizationIds.has(String(garden.id)))
        .forEach((garden) => {
          addSelectedGardenWithOptimization(garden);
        });
    },
    [
      addSelectedGardenWithOptimization,
      gardensWithOptimizations,
      removeSelectedGardenWithOptimization,
      selectedGardensWithOptimizations,
      setSelectedGardenIdsForOptimization,
    ],
  );

  const handleCancelAllOptimizations = useCallback(async () => {
    const gardenIds = selectedGardensWithOptimizations.map((garden) => String(garden.id));

    if (!gardenIds.length) return;

    try {
      const { data } = await optimizeManyGardens({
        gardenIds,
        optimize: {
          sun: false,
          rain: false,
          wind: false,
          active: false,
          limits: {
            max: {
              sun: 0,
              rain: 0,
              wind: 0,
            },
          },
        },
      });

      if (data.success !== true) {
        throw new Error(data.message || data.error || tr("Failed to cancel optimizations"));
      }

      notification.success({
        message: tr("Optimizations cancelled successfully"),
        placement: "topRight",
      });

      resetBulkOptimizationModalState();
      setSearchParameters((prev) => {
        prev.delete("gardens_optimizations");
        return prev;
      });
    } catch (error) {
      notification.error({
        message: error instanceof Error ? error.message : tr("Failed to cancel optimizations"),
        placement: "topRight",
      });
    }
  }, [optimizeManyGardens, resetBulkOptimizationModalState, selectedGardensWithOptimizations, setSearchParameters]);

  let transferWidth = 300;
  if (typeof width === "number") {
    if (width < 576) {
      transferWidth = 120;
    }
    if (width >= 576 && width < 768) {
      transferWidth = 200;
    }
    if (width >= 768 && width < 992) {
      transferWidth = 250;
    }
    if (width >= 992 && width < 1200) {
      transferWidth = 210;
    }
    if (width >= 1200) {
      transferWidth = 260;
    }
  }

  const areOptimizationCardsDisabled = selectedGardenIdsForOptimization.length === 0;
  const optimizationCardsDisabledMessage = tr("Add at least one garden to list to configure optimizations.");

  return (
    <OptimizationProvider>
      <Space direction="vertical" size={20} style={{ width: "100%" }}>
        <Row>
          <Col lg={15} md={24} sm={24} xs={24}>
            <Space direction="vertical">
              <Typography.Text>{tr("Select a garden to view optimizations")}</Typography.Text>

              <Transfer
                dataSource={/** @type {import("../types/global").Garden[]} */ (gardens)}
                listStyle={{
                  height: 400,
                  marginBottom: 30,
                  width: transferWidth,
                }}
                locale={{
                  itemUnit: "",
                  itemsUnit: "",
                  notFoundContent: tr("No gardens found"),
                  searchPlaceholder: tr("Park/Garden Search"),
                }}
                render={(/** @type {import("../types/global").Garden} item */ item) => (item.optimization &&
                  <Space key={item.id}>
                    <Typography.Text>{item.name}</Typography.Text>
                    {item.optimization.rain && (
                      <Tooltip title={tr("Rain Optimization Available")}>
                        <OptimizationProvider.Icon color="orange" optimizationName="rain" />
                      </Tooltip>
                    )}
                    {item.optimization.sun && <OptimizationProvider.Icon color="lightskyblue" optimizationName="sun" />}
                    {item.optimization.wind && <OptimizationProvider.Icon color="blueviolet" optimizationName="wind" />}
                  </Space>
                )}
                rowKey={(/** @type {import("../types/global").Garden} */ item) => String(item.id)}
                onChange={handleSelectedGardenIdsForOptimizationChange}
                showSearch
                targetKeys={selectedGardenIdsForOptimization}
                titles={[
                  <Typography.Text style={{ fontWeight: "bold" }}>{tr("All Gardens")}</Typography.Text>,
                  <Typography.Text style={{ fontWeight: "bold" }}>{tr("Selected Gardens")}</Typography.Text>,
                ]}
              />
            </Space>
          </Col>

          <Col lg={9} md={24} sm={24} xs={24}>
            <Space direction="vertical" size={10} style={{ display: "flex", flex: 1 }}>
              <Typography.Text>{tr("Optimizations for selected gardens")}</Typography.Text>

              <OptimizationProvider.CardAlternate
                disabled={areOptimizationCardsDisabled}
                dispabledMessage={optimizationCardsDisabledMessage}
                maximum={80}
                minimum={20}
                optimizationName="rain"
                unit="percent"
              />

              <OptimizationProvider.CardAlternate
                disabled={areOptimizationCardsDisabled}
                dispabledMessage={optimizationCardsDisabledMessage}
                maximum={55}
                minimum={35}
                optimizationName="sun"
                unit="celsius"
              />

              <OptimizationProvider.CardAlternate
                disabled={areOptimizationCardsDisabled}
                dispabledMessage={optimizationCardsDisabledMessage}
                maximum={100}
                minimum={10}
                optimizationName="wind"
                unit="kilometer-per-hour"
              />
            </Space>
          </Col>
        </Row>

        {!!selectedGardensWithOptimizations.length && (
          <Row>
            <Col xs={24}>
              <Alert
                action={
                  <Button danger type="primary" onClick={handleCancelAllOptimizations}>
                    {tr("Cancel Optimizations")}
                  </Button>
                }
                description={
                  <div style={{ marginTop: 10 }}>
                    {selectedGardensWithOptimizations.map((item) => (
                      <div key={item.id}>
                        <Space>
                          <Typography.Text>&bull; {item.name}</Typography.Text>

                          {item.optimization.rain && (
                            <Tooltip title={tr("Rain Optimization Available")}>
                              <OptimizationProvider.Icon color="orange" optimizationName="rain" />
                            </Tooltip>
                          )}

                          {item.optimization.sun && <OptimizationProvider.Icon color="lightskyblue" optimizationName="sun" />}

                          {item.optimization.wind && <OptimizationProvider.Icon color="blueviolet" optimizationName="wind" />}
                        </Space>
                      </div>
                    ))}
                  </div>
                }
                message={tr("These gardens will have their optimizations turned off")}
                type="error"
              />
            </Col>
          </Row>
        )}
      </Space>
    </OptimizationProvider>
  );
};

GardenProvider.WateringStatusModal = function GardenProviderWateringStatusModal() {
  const {
    bulkWateringStatusState,
    gardens,
    selectedGardenIdsForWateringStatus,
    setSelectedGardenIdsForWateringStatus,
  } = useGarden();
  const [bulkWateringStatus, setBulkWateringStatus] = bulkWateringStatusState;
  const { width } = useWindowSize();
  // Local to the modal on purpose: must not touch the map's URL "tags" filter.
  // Resets automatically on close because the parent Modal uses destroyOnClose.
  const [selectedTagIds, setSelectedTagIds] = useState(/** @type {string[]} */([]));

  // Same query key/request as MapsProvider so the tags cache is shared.
  // useMaps() is not available here: this modal renders outside MapsProvider.
  const { data: tags, isFetching: isFetchingTags } = useQuery({
    queryKey: ["Tags"],
    queryFn: () =>
      axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/tag`).then(
        (
          /** @type {import("axios").AxiosResponse<import("../types/global").GetTagsApiResponse>} */
          res,
        ) => res.data.tags,
      ),
  });

  // Tag filter only narrows the left (source) list. Gardens already in
  // "Selected Gardens" are always kept so they never vanish from the right list.
  const transferDataSource = useMemo(() => {
    const allGardens = /** @type {import("../types/global").Garden[]} */ (gardens) || [];
    if (!selectedTagIds.length) return allGardens;

    const selectedTagIdSet = new Set(selectedTagIds.map(String));
    const selectedGardenIdSet = new Set(selectedGardenIdsForWateringStatus.map(String));

    return allGardens.filter(
      (garden) =>
        selectedGardenIdSet.has(String(garden.id)) ||
        garden.tags?.some((tagId) => selectedTagIdSet.has(String(tagId))),
    );
  }, [gardens, selectedGardenIdsForWateringStatus, selectedTagIds]);

  let transferWidth = 300;
  if (typeof width === "number") {
    if (width < 576) transferWidth = 120;
    else if (width < 768) transferWidth = 200;
    else if (width < 992) transferWidth = 250;
    else if (width < 1200) transferWidth = 210;
    else transferWidth = 260;
  }

  const isActivating = bulkWateringStatus === "active";

  // Gardens whose status will actually flip — used for the summary line.
  const changingGardens = useMemo(
    () =>
      (gardens || []).filter(
        (garden) =>
          selectedGardenIdsForWateringStatus.some((id) => String(id) === String(garden.id)) &&
          garden.wateringStatus !== bulkWateringStatus,
      ),
    [bulkWateringStatus, gardens, selectedGardenIdsForWateringStatus],
  );

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <Row gutter={[20, 20]}>
        <Col lg={15} md={24} sm={24} xs={24}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Typography.Text>{tr("Select the gardens to update")}</Typography.Text>
            {!!tags?.length && (
              <Select
                allowClear
                disabled={isFetchingTags}
                dropdownStyle={{ zIndex: 1054 }}
                loading={isFetchingTags}
                mode="multiple"
                onChange={(value) => setSelectedTagIds(value)}
                optionFilterProp="label"
                options={tags.map((tag) => ({ label: tr(tag.name), value: tag.id }))}
                placeholder={tr("Filter gardens by tags")}
                showSearch
                style={{ width: "100%" }}
                value={selectedTagIds}
              />
            )}
            <Transfer
              dataSource={transferDataSource}
              filterOption={(inputValue, item) =>
                (item.name || "").toLowerCase().includes(inputValue.toLowerCase())
              }
              listStyle={{ height: 400, marginBottom: 30, width: transferWidth }}
              locale={{
                itemUnit: "",
                itemsUnit: "",
                notFoundContent: tr("No gardens found"),
                searchPlaceholder: tr("Park/Garden Search"),
              }}
              onChange={(nextTargetKeys) => setSelectedGardenIdsForWateringStatus(nextTargetKeys)}
              render={(/** @type {import("../types/global").Garden} */ item) => {
                const isActive = item.wateringStatus === "active";
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", overflow: "hidden" }}>
                    <Badge status={isActive ? "success" : "default"} style={{ flex: "0 0 auto" }} />
                    <Typography.Text
                      ellipsis={{
                        tooltip: {
                          title: (
                            <Space direction="vertical" size={0}>
                              <Typography.Text style={{ color: "#fff" }}>{item.name}</Typography.Text>
                              <Typography.Text style={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
                                {isActive ? tr("Active") : tr("Passive")}
                              </Typography.Text>
                            </Space>
                          ),
                          placement: "right",
                        },
                      }}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      {item.name}
                    </Typography.Text>
                  </div>
                );
              }}
              rowKey={(/** @type {import("../types/global").Garden} */ item) => String(item.id)}
              showSearch
              targetKeys={selectedGardenIdsForWateringStatus}
              titles={[
                <Typography.Text style={{ fontWeight: "bold" }}>{tr("All Gardens")}</Typography.Text>,
                <Typography.Text style={{ fontWeight: "bold" }}>{tr("Selected Gardens")}</Typography.Text>,
              ]}
            />
          </Space>
        </Col>
        <Col lg={9} md={24} sm={24} xs={24}>
          <Space direction="vertical" size={10} style={{ display: "flex", flex: 1 }}>
            <Typography.Text>{tr("Watering status to apply")}</Typography.Text>
            <Radio.Group
              buttonStyle="solid"
              onChange={(e) => setBulkWateringStatus(e.target.value)}
              optionType="button"
              options={[
                { label: tr("Active"), value: "active" },
                { label: tr("Passive"), value: "disabled" },
              ]}
              value={bulkWateringStatus}
            />
            <Alert
              description={
                isActivating
                  ? tr("Watering features for the devices in the selected gardens will be enabled.")
                  : tr("Watering features for the devices in the selected gardens will be disabled. Scheduled plans will not run while watering is passive.")
              }
              message={
                selectedGardenIdsForWateringStatus.length
                  ? `${changingGardens.length} / ${selectedGardenIdsForWateringStatus.length} ${tr("selected gardens will change")}`
                  : tr("Add at least one garden to list to apply a watering status.")
              }
              showIcon
              type={selectedGardenIdsForWateringStatus.length ? (isActivating ? "info" : "warning") : "info"}
            />
          </Space>
        </Col>
      </Row>
    </Space>
  );
};

GardenProvider.StatusSwitch = function GardenProviderStatusSwitch() {
  const { garden, isMutatingGarden, mutateGarden } = useGarden();

  if (!garden) return null;

  return (
    <Switch
      checked={garden?.wateringStatus === "active"}
      checkedChildren={tr(garden?.wateringStatus === "active" ? "Active" : "Passive")}
      disabled={isMutatingGarden}
      onChange={async (checked) =>
        await mutateGarden({
          id: garden.id,
          wateringStatus: checked ? "active" : "disabled",
        })
      }
      unCheckedChildren={tr(garden?.wateringStatus === "disabled" ? "Passive" : "Active")}
    />
  );
};

GardenProvider.GardenTitleInDetailsModal = function GardenProviderGardenTitleInDetailsModal() {
  const { garden, mutateGarden } = useGarden();

  if (!garden) return null;

  return (
    <Typography.Text
      editable={{
        icon: <EditOutlined style={{ transform: "translate(0, -2px)" }} />,
        onChange: /** @type {(name:string) => Promise<void>} - onChange is triggered onBlur */ (
          async (name) => {
            /** @type {import("../types/global").GardenUpdateRequest} */
            const _gardenRequest = {
              id: garden.id,
              name,
              geoJson: {
                ...garden.geoJson,
                properties: {
                  ...garden.geoJson.properties,
                  MAHAL_ADI: name,
                },
              },
            };

            await mutateGarden(_gardenRequest);
          }
        ),
        tooltip: tr("Edit Garden Name"),
      }}
      style={{ margin: 0 }}
    >
      {garden.name}
    </Typography.Text>
  );
};

/**
 * Returns the garden color based on the given garden's status.
 * @param {import("../types/global").Garden} garden -  The garden object.
 * @returns {string} - The class name based on the given garden's status.**/
export function getGardenAreaColor(garden) {
  if (garden.wateringStatus === "disabled") {
    // Garden passive
    return "#fecaca";
  } else {
    //Garden Active
    if (garden.devices.every((d) => d.plans.filter((p) => p.status === "active").length === 0)) {
      //No active plan
      return "#a8ff96ff";
    }

    if (garden.next24h && garden.next24h.length === 0) {
      //No Upcoming plan
      return "#fde68a";
    }

    if (garden.next24h && garden.next24h.length > 0) {
      //Upcoming plan
      return "#b6ceffff";
    }

    return "#b6ceffff";
  }
}

/**
 * Returns the garden color based on the given garden's status.
 * @param {import("../types/global").Garden} garden -  The garden object.
 * @returns {string} - The class name based on the given garden's status.**/
export function getGardenBorderColor(garden) {
  if (garden.wateringStatus === "disabled") {
    // Garden passive
    return "#fd8d8dff";
  } else {
    //Garden Active
    if (garden.devices.every((d) => !d.plans.some((p) => p.status === "active"))) {
      //No active plan
      return "#62dd5eff";
    }

    if (garden.next24h && garden.next24h.length === 0) {
      //No Upcoming plan
      return "#fde069ff";
    }

    if (garden.next24h && garden.next24h.length > 0) {
      //Upcoming plan
      return "#8fb4ffff";
    }

    return "#91b6ffff";
  }
}
